// 멤버십 구독료·결제 서버 호출 래퍼 — 2026-09-07 (계약: docs/ops/toss-payments-impl-spec.md §3-3·§4)
import { supabase } from "./supabaseClient";

// functions.invoke 는 non-2xx 일 때 error.context(Response) 에 본문이 있다 — {code, message} 를 꺼내 Error 로 만든다.
async function invoke(name, body) {
  // 2026-09-22 실측: billing-refund-self 가 401 로 떨어졌다(환불 요청이 requested 로 멈춤).
  // supabase-js 가 functions 클라이언트에 세션 토큰을 못 붙인 경우가 있어 **호출할 때마다 직접 붙인다**.
  // RPC(PostgREST)는 같은 토큰으로 통과했으므로 세션은 살아 있었다 — 원인은 functions 쪽 헤더였다.
  let authHeaders;
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) authHeaders = { Authorization: `Bearer ${token}` };
  } catch (_) { /* 세션을 못 읽으면 기존 동작 그대로 */ }
  if (!supabase.functions || typeof supabase.functions.invoke !== "function") {
    const e = new Error("데모에서는 결제할 수 없습니다");
    e.code = "DEMO";
    throw e;
  }
  const { data, error } = await supabase.functions.invoke(name, authHeaders ? { body, headers: authHeaders } : { body });
  if (error) {
    let payload = null;
    try {
      if (error.context && typeof error.context.json === "function") payload = await error.context.json();
    } catch (e) { /* ignore */ }
    const err = new Error(payload?.message || error.message || `${name} 호출 실패`);
    err.code = payload?.code || "FUNCTION_ERROR";
    err.payload = payload;
    throw err;
  }
  if (data && data.code && data.ok === undefined && !data.order_id && !data.processed) {
    const err = new Error(data.message || data.code);
    err.code = data.code;
    err.payload = data;
    throw err;
  }
  return data;
}

async function rpc(name, args) {
  const { data, error } = await supabase.rpc(name, args || {});
  if (error) {
    const err = new Error(error.message);
    err.code = error.code;
    err.hint = error.hint;
    throw err;
  }
  return data;
}

// extra: 등급 승급권은 { no_refund_ack: true, ack_text } 필수(서버가 없으면 거절) — 대표 2026-09-21 환불 불가
export const createOrder = (kind, promoCode, orderId, extra) => invoke("payment-order-create", { kind, promo_code: promoCode || null, order_id: orderId || null, ...(extra || {}) });
export const confirmPayment = (paymentKey, orderId, amount) => invoke("payment-confirm", { payment_key: paymentKey, order_id: orderId, amount: Number(amount) });
export const issueBillingKey = (authKey, customerKey) => invoke("billing-issue", { auth_key: authKey, customer_key: customerKey });
export const chargeNow = () => invoke("billing-charge", { scope: "self" });

export const fetchOverview = (email) => rpc("billing_overview", email ? { p_email: email } : {});
// 등급 승급권 판매 상태 — { ok, open, from, months, seats_total, seats_left, grades[], mine } (2026-09-22)
// 멤버십과 다른 스위치라 billing_overview 와 따로 읽는다. 실패하면 화면이 닫힌 상태로 보이게 훅이 { ok:false } 를 쓴다.
// 재활성화 견적 — 재활성화비(1회) + 그달 멤버십 구독료(일할). 🔒 금액은 서버에서만 만든다(대표 2026-09-23)
export const fetchReactivationQuote = (email) => rpc("billing_reactivation_quote", email ? { p_email: email } : {});

// 🔒 대상 이메일을 넘긴다 — 안 넘기면 서버가 로그인 계정을 봐서, 운영팀이 「회원 화면 보기」로 볼 때 어드민 본인의 자격이 뜬다(2026-10-28).
export const fetchAccessPassOffer = (email) => rpc("access_pass_offer", email ? { p_email: email } : {}).then((d) => d || { ok: false });
// 토스 customerKey — billing_customer 는 본인 행 SELECT 가 열려 있다(RLS). 없으면 billing_overview 가 만들어 둔다.
export async function fetchCustomerKey() {
  const { data, error } = await supabase.from("billing_customer").select("customer_key").limit(1).maybeSingle();
  if (error) throw new Error(error.message);
  if (!data?.customer_key) throw new Error("고객 키가 없습니다. 화면을 새로고침한 뒤 다시 시도해 주세요.");
  return data.customer_key;
}
export const consentPriceChange = (id) => rpc("billing_consent_price_change", { p_change_id: id });
export const declinePriceChange = (id) => rpc("billing_decline_price_change", { p_change_id: id });
// 해지 = 해지 예약(종료일 전날까지 이용 · 그 뒤 청구 없음). requestRefund=true 면 7일 안전창일 때 환불 요청을 같이 남긴다 →
// 이어서 requestSelfRefund() 가 서버에서 다시 판정해 조건이 맞으면 바로 환불한다(대표 2026-09-21 「조건 맞으면 자동 환불」). 실패분만 어드민 환불 탭으로.
export const cancelSubscription = (requestRefund = false) => rpc("billing_cancel_subscription", { p_request_refund: Boolean(requestRefund) });
export const requestSelfRefund = () => invoke("billing-refund-self", {});
export const undoCancel = () => rpc("billing_undo_cancel");
// 해지 사유(해지 뒤 선택형) — 2026-09-22
export const setCancelReason = (code, note) => rpc("billing_set_cancel_reason", { p_code: code, p_note: note || null });
export const cancelReasonGiven = () => rpc("billing_cancel_reason_given");
// 결제 전 주문 취소(pending → canceled, 토스 호출 없음) — 2026-09-22
export const cancelPendingOrder = (orderId) => rpc("billing_cancel_pending_order", { p_order_id: orderId });
// 카드 등록 전 청약철회 제한 사전 고지 동의 기록(billing_event_log kind='refund_notice_ack'). 1회성 결제는 주문의 ack 컬럼에 남는다.
export const ackRefundNotice = (text) => rpc("billing_ack_refund_notice", { p_text: text });
// 기존 회원 유료 전환 동의(10월 동의 창을 놓친 경우) — documentHash 는 PaidChangeConsentGate 와 같은 canonicalPaidChangeText 의 SHA-256
export const consentPaidChange = (documentHash) => rpc("billing_consent_paid_change", { p_document_hash: documentHash || null });
